import produce from 'immer'
import { actionTypes } from '../actions/orderActions'
import {
    StorageKeys,
    ORDER_METHOD,
    PAYMENT_METHOD,
    APP_SERVICE,
    ACCESS_SERVICES_MAP,
    REQUEST_KINDS,
} from '../constants/index'
import { setObjectData, getObjectData, setStringData, getStringData } from '../utils/localStorageHelper'
import { getStringFromFood, getUserAddressFromAddressComp, validFoodDataFromServer } from '../utils'
import { actionTypes as accountActionTypes } from '../actions/accountActions'
import { actionTypes as restaurantActionTypes } from '../actions/restaurantActions'
import { getInitMethod, getPaymentMethodsSupport } from '../utils/restaurant'
import paths from '../routes/paths'

const uncache = {
    tmpFoods: [],
    viewFoodDetails: null,
    ordered: [],
    initMethod: true,
    printerSelectData: {}
}

const defaultState = {
    orderMethod: '',
    tableID: '',
    deliverPosition: '',
    ordering: [],
    restaurantPath: '/',
    lastTimeRequestPayment: '',
    lastTimeRequestSupporter: '',
    firebaseUrl: '',
    groupOrder: 1,
    listGroupOrder: [1],
    expireTime: '',
    tabletForm: {
        paymentMethod: '',
        orderMethod: '',
    },
    ...uncache,
}

const getInitState = () => {
    const preData = getObjectData(StorageKeys.order)

    return { ...preData, ...uncache }
}

const countFoodFromGroup = (foods, group) =>
    foods.filter(f => f.groupOrder === group).length

const initialState = getInitState()

const convertAddress = address => {
    const data = {
        userAddressNumber: address.addressNumber,
        userStreet: address.street,
        userCity: address.city,
        userPostcode: address.zipCode,
        floor: address.floor,
        userPhone: address.phone,
    }

    return Object.keys(data).reduce(
        (rs, key) => (data[key] ? { ...rs, [key]: data[key] } : rs),
        {}
    )
}

const sortByGroup = (a, b) => a.groupOrder - b.groupOrder;

const defaultIfEmpty = (arr, defaultArr) => {
    return arr?.length ? arr : defaultArr;
}

const OrderReducer = (state = initialState, action) => {
    let newChange = true
    const newState = produce(state, draft => {
        switch (action.type) {
            case actionTypes.SET_RESTAURANT_PATH: {
                const { restaurantPath } = action.payload
                if (draft.restaurantPath !== restaurantPath) {
                    return {
                        ...defaultState,
                        restaurantPath,
                    }
                } else {
                    draft.restaurantPath = restaurantPath
                }
                break
            }

            case restaurantActionTypes.SET_RESTAURANT_INFO: {
                const paymentMethods = getPaymentMethodsSupport(action.payload)
                const initPaymentMethods = getInitMethod(paymentMethods)

                if (APP_SERVICE === ACCESS_SERVICES_MAP.WEB && !draft.deliverPosition?.paymentMethod) {
                    draft.deliverPosition = {
                        ...draft.deliverPosition,
                        paymentMethod: initPaymentMethods
                    }
                }

                if (APP_SERVICE === ORDER_METHOD.TABLE_QRCODE && !draft.tabletForm.paymentMethod) {
                    draft.tabletForm.paymentMethod = initPaymentMethods
                }

                break
            }

            case actionTypes.UPDATE_ORDER_METHOD_REDUX: {
                draft.orderMethod = action.payload.method;
                break
            }
            case actionTypes.UPDATE_ORDER: {
                draft.ordering = action.payload.orderingUpdate;
                break
            }

            // qrcode at table
            case actionTypes.VERIFY_TABLE_SUCCESS: {
                const {
                    id,
                    hash,
                    tableNumber,
                    tokenOrderForWeb,
                    exppiredTime,
                    customerPosition,
                    foodData
                } = action.payload

                if (draft.tableID && draft.tableID !== id) {
                    draft.ordering = []
                    draft.ordered = []
                    draft.groupOrder = 1
                    draft.listGroupOrder = [1]
                } else {
                    draft.ordering = [
                        ...validFoodDataFromServer(foodData, customerPosition),
                        ...draft.ordering.filter(el => !el.submitted),
                    ].sort(sortByGroup)
                    const maxGroupOrder = Math.max(...draft.ordering.map(order => order.groupOrder), draft.listGroupOrder.slice(-1)[0]);
                    draft.listGroupOrder = defaultIfEmpty([ ...new Array(maxGroupOrder) ].map((_, index) => index + 1), [1]);
                }
                draft.hash = hash
                draft.tableNumber = tableNumber
                draft.tokenOrderForWeb = tokenOrderForWeb
                draft.expireTime = new Date().getTime() + exppiredTime

                draft.customerPosition = customerPosition
                draft.tableID = id
                draft.orderMethod = ORDER_METHOD.QR_CODE
                draft.deliverPosition = ''
                break
            }

            case actionTypes.CHECK_TABLE_VALID_SUCCESS: {
                const {
                    foodData,
                    customerPosition
                } = action.payload;

                draft.ordering = [
                    ...validFoodDataFromServer(foodData, customerPosition),
                    ...draft.ordering.filter(el => !el.submitted),
                ].sort(sortByGroup)
                const maxGroupOrder = Math.max(...draft.ordering.map(order => order.groupOrder), draft.listGroupOrder.slice(-1)[0]);
                draft.listGroupOrder = defaultIfEmpty([ ...new Array(maxGroupOrder) ].map((_, index) => index + 1), [1]);

                break;
            }

            case actionTypes.CLEAR_DISTANCE: {
                draft.deliverPosition && (draft.deliverPosition.distance = undefined);
                break;
            }

            // qrcode at restaurant
            case actionTypes.VERIFY_QRCODE_SUCCESS: {
                const { hash, tokenOrderForWeb, firebaseUrl, exppiredTime } =
                    action.payload

                draft.ordering = []
                draft.ordered = []
                draft.hash = hash
                draft.tokenOrderForWeb = tokenOrderForWeb
                draft.firebaseUrl = firebaseUrl
                draft.expireTime = new Date().getTime() + exppiredTime

                draft.orderMethod = ORDER_METHOD.TABLE_QRCODE
                draft.deliverPosition = ''
                break
            }

            case actionTypes.SET_DELIVER_POSITION: {
                const {
                    addressComponents,
                    name,
                    fromSearchForm,
                    addressData,
                    latitude,
                    longitude,
                    ...rest
                } = action.payload

                let addressFromSelect = {}
                if (addressData) {
                    addressFromSelect = {
                        selectAddress: addressData.selectAddress,
                        ...convertAddress(addressData),
                    }
                }

                let data = {
                    ...rest,
                }

                if (latitude !== undefined) {
                    data.latitude = latitude;
                }
                if (longitude !== undefined) {
                    data.longitude = longitude;
                }

                // from search form
                if (!!addressComponents && fromSearchForm) {
                    const { userCity, userPostcode, userStreet, userAddressNumber } = getUserAddressFromAddressComp(addressComponents);

                    data = {
                        ...data,
                        userCity,
                        userAddressNumber,
                        userStreet,
                        selectAddress: '-1',
                        userPostcode,
                        floor: '',
                    }
                }

                if (
                    data.selectAddress === '-1' &&
                    draft.deliverPosition.selectAddress &&
                    data.selectAddress !== draft.deliverPosition.selectAddress
                ) {
                    data.distance = ''
                }

                draft.deliverPosition = {
                    ...draft.deliverPosition,
                    ...data,
                    ...addressFromSelect,
                }
                break
            }

            case actionTypes.REQUEST_PAYMENT_SUCCESS: {
                // draft.ordering = []
                // draft.ordered = []
                if (action.payload.kind === REQUEST_KINDS.supporter) {
                    draft.lastTimeRequestSupporter = new Date().getTime();
                }
                if (action.payload.kind === REQUEST_KINDS.payment) {
                    draft.lastTimeRequestPayment = new Date().getTime();
                }
                break
            }

            case actionTypes.SUBMIT_ORDER_SUCCESS: {
                const { customerPosition, foodData } = action.payload
                draft.customerPosition = customerPosition

                if (draft.orderMethod === ORDER_METHOD.QR_CODE) {
                    draft.ordering = [
                        ...draft.ordering.filter(el => el.submitted),
                        ...foodData.map(el => {
                            el.submitted = true
                            return el
                        }),
                    ].sort(sortByGroup);
                } else {
                    draft.ordering = []
                    draft.groupOrder = 1
                    draft.listGroupOrder = [1]
                }
                break
            }

            case actionTypes.SUBMIT_ORDER_TAKE_A_GO_SUCCESS: {
                draft.ordering = []
                break
            }

            case actionTypes.ADD_FOOD_TO_CART: {
                let listFood = action.payload

                if (draft.tmpFoods.length) {
                    const { tmpIndex, ...newFood } = action.payload[0]

                    for (let food of draft.tmpFoods) {
                        food.amount = newFood.amount
                    }

                    draft.tmpFoods.splice(tmpIndex, 1, newFood)
                    const index = draft.tmpFoods.findIndex(
                        el => !el.beilageItems
                    )

                    if (index > -1) {
                        break
                    }

                    listFood = [...draft.tmpFoods]
                    draft.tmpFoods = []
                }

                let needSort = false

                for (let food of listFood) {
                    const { index, ...data } = food
                    if (index !== -1) {
                        // draft.ordering.splice(index, 1, data)
                        draft.ordering[index] = {
                            ...draft.ordering[index],
                            ...data,
                        }
                    } else {
                        data.groupOrder = draft.groupOrder
                        let i = draft.ordering.findIndex(
                            f =>
                                !f.submitted &&
                                getStringFromFood(f) === getStringFromFood(data)
                        )
                        if (i !== -1) {
                            draft.ordering[i].amount += data.amount
                        } else {
                            draft.ordering.push({
                                ...data,
                                idInCart: new Date().getTime()
                            })
                            needSort = true
                        }
                    }
                }

                needSort &&
                    draft.ordering.sort(sortByGroup)
                break
            }

            case actionTypes.UPDATE_AMOUNT: {
                const { index, id, amount, groupOrder } = action.payload;

                let indexWillUpdate = index;

                if (index !== 0 && !index && id) {
                    indexWillUpdate = draft.ordering.findIndex(
                        f =>
                            !f.submitted &&
                            f.foodDetails?.id === id &&
                            f.groupOrder === groupOrder
                    );
                }

                if (indexWillUpdate !== -1) {
                    const newAmount = amount === 0 ? 0 : draft.ordering[indexWillUpdate].amount + amount;
                    if (newAmount) {
                        draft.ordering[indexWillUpdate].amount = newAmount;
                    } else {
                        draft.ordering.splice(indexWillUpdate, 1);
                    }

                    if (!countFoodFromGroup(draft.ordering, groupOrder)) {
                        draft.listGroupOrder = defaultIfEmpty(draft.listGroupOrder
                            .filter(el => el !== groupOrder)
                            .map(el => el > groupOrder ? el - 1 : el), [1]);

                        draft.ordering = draft.ordering.map(f => ({ ...f, groupOrder: f.groupOrder > groupOrder ? f.groupOrder - 1 : f.groupOrder }));

                        draft.groupOrder >= groupOrder && (draft.groupOrder = Math.max(draft.groupOrder - 1, 1));
                    }
                }
                break
            }

            case actionTypes.UPDATE_NOTE: {
                const { note, id, index, groupOrder } = action.payload
                let indexWillUpdate = index

                if (indexWillUpdate === undefined) {
                    indexWillUpdate = draft.ordering.findIndex(
                        el =>
                            !el.submitted &&
                            el.foodDetails?.id === id &&
                            el.groupOrder === groupOrder
                    )
                }

                draft.ordering[indexWillUpdate] &&
                    (draft.ordering[indexWillUpdate].note = note)
                break
            }

            case actionTypes.VERIFY_QRCODE:
            case actionTypes.VERIFY_TABLE: {
                draft.initMethod = false
                draft.expireTime = ''
                break
            }

            case actionTypes.REMOVE_ITEM: {
                const { index } = action.payload
                draft.ordering.splice(index, 1)
                break
            }

            case actionTypes.SET_TMP_FOODS: {
                newChange = false
                draft.tmpFoods = action.payload
                break
            }

            case actionTypes.GET_ORDERS_QRCODE_SUCCESS: {
                draft.ordering = [
                    ...action.payload,
                    ...draft.ordering.filter(el => !el.submitted),
                ].sort(sortByGroup)
                const maxGroupOrder = Math.max(...draft.ordering.map(order => order.groupOrder), draft.listGroupOrder.slice(-1)[0]);
                draft.listGroupOrder = defaultIfEmpty([ ...new Array(maxGroupOrder) ].map((_, index) => index + 1), [1]);
                break
            }

            case actionTypes.SET_VIEW_FOOD_DETAILS: {
                newChange = false
                draft.viewFoodDetails = action.payload
                break
            }

            case actionTypes.ADD_GROUP_ORDER: {
                const lastGroup = draft.listGroupOrder.slice(-1)[0]
                if (countFoodFromGroup(draft.ordering, lastGroup)) {
                    const newGroup = lastGroup + 1
                    draft.groupOrder = newGroup
                    draft.listGroupOrder.push(newGroup)
                }
                break
            }

            case actionTypes.SELECT_GROUP_ORDER: {
                const groupId = action.payload.groupId
                if (
                    draft.listGroupOrder.includes(groupId) &&
                    groupId !== draft.groupOrder
                ) {
                    draft.groupOrder = groupId
                }
                break
            }

            case actionTypes.DEL_GROUP_ORDER: {
                const groupId = action.payload.groupId
                if (draft.listGroupOrder.includes(groupId)) {
                    draft.ordering = draft.ordering
                        .filter(el => el.groupOrder !== groupId)
                        .map(f => ({ ...f, groupOrder: f.groupOrder > groupId ? f.groupOrder - 1 : f.groupOrder }));

                    draft.listGroupOrder = defaultIfEmpty(draft.listGroupOrder
                        .filter(el => el !== groupId)
                        .map(el => el > groupId ? el - 1 : el), [1]);
                    draft.groupOrder >= groupId && (draft.groupOrder = Math.max(draft.groupOrder - 1, 1));
                }
                break
            }

            case actionTypes.CLEAR_EXPIRE_TIME: {
                draft.expireTime = ''

                if (
                    [ORDER_METHOD.PICKUP, ORDER_METHOD.DELIVERY].includes(
                        draft.orderMethod
                    )
                ) {
                    draft.orderMethod = ''
                    draft.initMethod = false
                }
                break
            }

            case accountActionTypes.GET_PROFILE_SUCCESS: {
                // if (
                //     action.payload.userAddresses?.length &&
                //     !draft.deliverPosition.selectAddress
                // ) {
                //     const lastIndex = action.payload.userAddresses.length - 1
                //     const address = action.payload.userAddresses[lastIndex]

                //     !draft.deliverPosition && (draft.deliverPosition = {})

                //     draft.deliverPosition = {
                //         ...draft.deliverPosition,
                //         ...convertAddress(address),
                //         selectAddress: lastIndex,
                //     }
                // }
                break
            }

            case actionTypes.SET_TABLET_ORDER_METHOD: {
                draft.tabletForm.orderMethod = action.payload
                break
            }

            case actionTypes.SET_TABLE_FORM: {
                draft.tabletForm = { ...draft.tabletForm, ...action.payload }
                break
            }

            case actionTypes.SET_PRINTER_SELECT_DATA: {
                newChange = false
                draft.printerSelectData = action.payload
                break
            }

            case actionTypes.UPDATE_FOOD_PRINTER: {
                const { printer, idInCart } = action.payload;
                const foodIndex = draft.ordering.findIndex(el => el.idInCart === idInCart);
                if (foodIndex !== -1) {
                    draft.ordering[foodIndex].printerSelected = printer;
                    draft.ordering[foodIndex].printerIp = printer?.number + "";
                }
                break
            }

            case actionTypes.RESET_CART: {
                draft.ordering = [];
                draft.ordered = [];
                draft.groupOrder = 1;
                draft.listGroupOrder = [1];
                break
            }

            case actionTypes.CLEAR_ALL_ORDER_DATA: {
                return {
                    ...defaultState,
                    restaurantPath: state.restaurantPath // giữ lại path nếu cần
                }
            }

            default: {
                newChange = false
                return state
            }
        }
    })
    if (newChange) {
        setObjectData(StorageKeys.order, newState)
    }
    return newState
}

export default OrderReducer
